import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import Badge from "../common/Badge";
import { useStore } from "../../context/StoreProvider";
import { formatPrice, cn, discountPercent } from "../../lib/utils";

const ProductCard = ({ product }) => {
  const { inWishlist, toggleWishlist } = useStore();
  const [hover, setHover] = useState(false);
  const liked = inWishlist(product.id);
  const secondImage = product.images[1] || product.images[0];
  const off = discountPercent(product.price, product.compareAtPrice);

  return (
    <div className="group relative" data-testid={`product-card-${product.slug}`}>
      <Link
        to={`/product/${product.slug}`}
        aria-label={product.name}
        className="block"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-cream">
          <img
            src={hover ? secondImage : product.images[0]}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
          {/* Badges */}
          <div className="absolute left-3 top-3 flex flex-col gap-1.5">
            {product.badges?.map((b) => (
              <Badge key={b} label={b} />
            ))}
          </div>
        </div>
      </Link>

      {/* Wishlist */}
      <button
        onClick={() => toggleWishlist(product.id)}
        aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
        aria-pressed={liked}
        data-testid={`wishlist-toggle-${product.slug}`}
        className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center bg-paper/90 backdrop-blur transition-colors hover:bg-paper"
      >
        <Heart
          size={17}
          className={cn(liked ? "fill-ink text-ink" : "text-ink")}
        />
      </button>

      {/* Meta */}
      <div className="mt-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={`/product/${product.slug}`}>
            <h3 className="truncate text-sm font-medium leading-snug">
              {product.name}
            </h3>
          </Link>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-mono text-sm">{formatPrice(product.price)}</span>
            {product.compareAtPrice && (
              <span className="font-mono text-xs text-ink/40 line-through">
                {formatPrice(product.compareAtPrice)}
              </span>
            )}
            {off > 0 && (
              <span className="font-mono text-[11px] text-brand-dark">-{off}%</span>
            )}
          </div>
        </div>
        {/* Color swatches */}
        <div className="flex shrink-0 items-center gap-1 pt-0.5">
          {product.colors.slice(0, 4).map((c) => (
            <span
              key={c.name}
              title={c.name}
              className="h-3.5 w-3.5 rounded-full border border-ink/20"
              style={{ backgroundColor: c.hex }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
